import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, notification, Spin, Select } from 'antd';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchUserById, updateUserById, fetchBranches } from '../api';
import RoleSelect from '../../../components/UI/RoleSelect';
import { PermissionFormItem } from 'components';

const MODULE_OPTIONS = [
  { label: "Billing", value: "Bill" },
  { label: "Invoice", value: "Invoice" },
  // ...add more as needed
];

const REGION_OPTIONS = [
  { label: "East", value: "East" },
  { label: "West", value: "West" },
  { label: "North", value: "North" },
  { label: "South", value: "South" },
  // ...add more as needed
];

const UserEdit = () => {
  const navigate = useNavigate();
  const { canEditModule, can, canViewModule } = usePermissions();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { id: paramId } = useParams();
  const location = useLocation();
  const userRecord = location.state?.userRecord;
  const id = paramId || userRecord?._id;
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    loadBranches();
    const fetchUser = async () => {
      try {
        const data = await fetchUserById(id);
        setUser(data);
      } catch (err) {
        notification.error({ message: 'Failed to load user data' });
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchUser();
  }, [id]);

   const loadBranches = async () => {
      try {
        const response = await fetchBranches({});
        if (response.success) {
          setBranches(response.data);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
      }
    };

  const handleSubmit = async (values) => {
    try {
      // Find the selected branch to get its name
      const selectedBranch = branches.find(branch => branch._id === values.branch);
      
      // Add branchName to the values
      const formData = {
        ...values,
        branchName: selectedBranch?.name || ''
      };
      
      await updateUserById(id, formData);
      notification.success({ message: 'User updated' });
      navigate('/users');
    } catch (error) {
      notification.error({ message: 'An error occurred while updating user' });
    }
  };

  if (loading) return <Spin size="large" />;

  return (
    <Form
      initialValues={user}
      onFinish={handleSubmit}
      layout="vertical"
      style={{ maxWidth: 600, margin: 'auto' }}
    >
      <PermissionFormItem module="user" name="name" label="Name">
        <Input />
      </PermissionFormItem>
      <PermissionFormItem module="user" name="email" label="Email">
        <Input />
      </PermissionFormItem>
      <PermissionFormItem module="user" name="adhar" label="Aadhaar">
        <Input />
      </PermissionFormItem>
      <PermissionFormItem module="user" name="pan" label="PAN">
        <Input />
      </PermissionFormItem>
      <PermissionFormItem module="user" name="branch" label="Branch">
        <Select placeholder="Select branch">
          {branches.map((branch) => (
            <Select.Option key={branch._id} value={branch._id}>
              {branch.name} ({branch.code})
            </Select.Option>
          ))}
        </Select>
      </PermissionFormItem>
      <PermissionFormItem module="user" name="region" label="Region">
        <Select placeholder="Select region (optional)">
          {REGION_OPTIONS.map((r) => (
            <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>
          ))}
        </Select>
      </PermissionFormItem>
      <PermissionFormItem module="user" name="role" label="Role">
        <RoleSelect allowCreate />
      </PermissionFormItem>
      <PermissionFormItem module="user" name="modules" label="Modules">
        <Select mode="multiple" placeholder="Select modules">
          {MODULE_OPTIONS.map((m) => (
            <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>
          ))}
        </Select>
      </PermissionFormItem>
      <PermissionFormItem module="user" name="password" label="Password" >
        <Input.Password />
      </PermissionFormItem>
      <Button type="primary" htmlType="submit">
        Save Changes
      </Button>
    </Form>
  );
};

export default UserEdit;
